<template>
  <section class="excerpt-block">
    <router-link
      :to="view.excerptRoute"
      class="excerpt-block__inner"
      :class="{ 'excerpt-block__inner--safari': isSafari }"
      @click="view.trackExcerptClick()"
      @click.middle="view.trackExcerptClick()"
    >
      <div class="excerpt-block__content">
        <div class="excerpt-block__quote-icon-block">
          <span class="iconfont iquote excerpt-block__quote-icon"></span>
        </div>
        <p class="excerpt-block__text">{{ view.data.excerpt_text }}</p>
        <div class="excerpt-block__metadata-block">
          <div>
            <div class="excerpt-block__metadata">
              {{ view.data.doc_title }} &#8226; {{ view.data.author }}
            </div>
            <div
              class="iconfont iarrow-long-right excerpt-block__open-icon"
            ></div>
          </div>
          <img
            v-lazy="{ src: view.data.cover_image }"
            crossorigin="anonymous"
            class="excerpt-block__image excerpt-block__image--small-screen"
            alt=""
          />
        </div>
      </div>
      <BookCover
        :doc="view.coverData"
        class="excerpt-block__image excerpt-block__image--large-screen"
      />
      <BookCardBackground
        v-if="!isSafari"
        class="excerpt-block__background"
        :doc="view.coverData"
      />
    </router-link>
  </section>
</template>

<script setup lang="ts">
import { onBeforeMount, ref } from 'vue'

import type { ExcerptView } from '@/models/excerpt'
import { platform } from '@/services/platform'
import { isSafari as _isSafari } from '@/helpers/userAgent'

import BookCover from '@/app/components/book/card/BookCover.vue'
import BookCardBackground from '@/app/components/book/card/BookCardBackground.vue'

defineProps<{
  view: ExcerptView
}>()

const isSafari = ref(false)

onBeforeMount(async () => {
  isSafari.value = (await platform.isIOS()) || _isSafari()
})
</script>

<style scoped lang="scss">
.excerpt-block__inner {
  padding: 40px 40px 32px;
  gap: 40px;
  border-radius: 12px;
  position: relative;
  border: 1px solid $c-white;
  display: flex;
  align-items: start;
  justify-content: space-between;

  @include hover {
    border-color: $c-border-2;
    .excerpt-block__image {
      transform: scale(1.07);
    }
  }

  @media only screen and (max-width: $w-mobile-l) {
    padding: 28px 20px 32px;
  }
}

.excerpt-block__inner--safari {
  background: $c-black-5;
}

.excerpt-block__quote-icon-block {
  position: absolute;
  font-size: 34px;
  width: 34px;
  height: 34px;
  top: -15px;
  left: 40px;

  @media only screen and (max-width: $w-mobile-l) {
    font-size: 30px;
    width: 30px;
    height: 30px;
    left: 20px;
  }
}

.excerpt-block__quote-icon {
  width: 34px;
  height: 34px;
  color: $c-black-3;

  @media only screen and (max-width: $w-mobile-l) {
    width: 30px;
    height: 30px;
  }
}

.excerpt-block__text {
  font-style: normal;
  font-weight: 400;
  font-size: 20px;
  line-height: 24px;
  letter-spacing: -0.02em;
  color: $c-black-2;
  margin-bottom: 0;

  @media only screen and (max-width: $w-mobile-l) {
    font-size: 16px;
  }
}

.excerpt-block__metadata {
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  display: flex;
  align-items: flex-end;
  color: $c-black-3;
}

.excerpt-block__open-icon {
  font-size: 24px;
  color: $c-black-3;
  margin-top: 20px;
}

.excerpt-block__image {
  --cover-width: 87px;
  margin-left: 20px;
  filter: $shadow-f;
  border-radius: 10px;
  width: var(--cover-width);
  transition: all 0.2s ease-in-out;
  @media only screen and (max-width: $w-tablet-s) {
    --cover-width: 68px;
    border-radius: 4px;
  }

  &--large-screen {
    @media only screen and (max-width: $w-mobile-l) {
      display: none;
    }
  }

  &--small-screen {
    @media only screen and (min-width: 681px) {
      display: none;
    }
  }
}

.excerpt-block__metadata-block {
  display: flex;
  align-items: start;
  justify-content: space-between;
}

.excerpt-block__metadata-block {
  margin-top: 20px;
}

.excerpt-block__background {
  border-radius: 12px;
}

.night-mode {
  .excerpt-block__inner {
    border-color: $c-dark-1;
    @include hover {
      border-color: $c-dark-8;
    }
  }

  .excerpt-block__inner--safari {
    background: $c-dark-2;
  }

  .excerpt-block__text {
    color: $c-black-11;
  }

  .excerpt-block__metadata,
  .excerpt-block__quote-icon,
  .excerpt-block__open-icon {
    color: $c-dark-7;
  }
}
</style>
